import { memo, useState } from 'react';
import { CARD_IMG_BASE, cardSrc } from './cards/cardUtils';
import { roundBook, fmtBook } from '../../utils/casinoBook';
const REMARK_ICON = 'https://wver.sprintstaticdata.com/v230/static/front/img/icons/remark.png'

function findByNat(sub, nat) {
  return sub.find((s) => s.nat === nat);
}

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}

function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}

function fmtAmt(n) {
  const x = Number(n) || 0;
  if (x >= 100000) return `${x / 100000}L`;
  if (x >= 1000) return `${x / 1000}K`;
  return String(x);
}

function bookOf(exposures, name, sid) {
  return roundBook(exposures?.[`sid:${sid}`] ?? exposures?.[name] ?? 0);
}

function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

const CARD_LABELS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

// range collapse (info + icon-range) — top uses "Range:", others "R:"
function Range({ rangeId, min, max, label = 'R:' }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="float-right">
      <i
        data-toggle="collapse"
        data-target={`#${rangeId}`}
        className="fas fa-info-circle"
        onClick={() => setOpen((o) => !o)}
      ></i>
      {' '}
      <div id={rangeId} className={`collapse icon-range${open ? ' show' : ''}`}>
        {label}
        <span>{fmtAmt(min)}</span>
        -
        <span>{fmtAmt(max)}</span>
      </div>
    </div>
  );
}

// dtobx-top box: Dragon / Tie / Tiger / Pair — book always visible (book-black)
function TopBox({ cls, label, item, exposures, onBet }) {
  const book = bookOf(exposures, item?.nat, item?.sid);
  return (
    <div
      className={`${cls}${suspCls(item)}`}
      onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'back', item.b, item)}
    >
      <div><b>{label}</b></div>
      {' '}
      <div className="text-center">
        <span className="d-block"><b>{item?.b ?? 0}</b></span>
        {' '}
        <span className={`d-block casino-book ${bookCls(book)}`}>{fmtBook(book)}</span>
      </div>
    </div>
  );
}

const DragonTigerSide = memo(function DragonTigerSide({
  label, labelClass, oddsRangeId, cardRangeId,
  even, odd, black, red, cardItems, cardOdds, onBet, exposures,
}) {
  const bEven = bookOf(exposures, even?.nat, even?.sid);
  const bOdd = bookOf(exposures, odd?.nat, odd?.sid);
  const bBlack = bookOf(exposures, black?.nat, black?.sid);
  const bRed = bookOf(exposures, red?.nat, red?.sid);

  return (
    <>
      <div>
        <div className="casino-box-row justify-content-center casino-odds casino-title">
          <div className="text-left w-100">
            <b className={labelClass}>{label}</b>
            {' '}
            <Range rangeId={oddsRangeId} min={even?.min ?? 100} max={even?.max ?? 10000} />
          </div>
        </div>

        <div className="casino-box-row">
          <div className="casino-bl-box"><b>{even?.b ?? 0}</b></div>
          <div className="casino-bl-box"><b>{odd?.b ?? 0}</b></div>
          <div className="casino-bl-box"><b>{black?.b ?? 0}</b></div>
          <div className="casino-bl-box"><b>{red?.b ?? 0}</b></div>
        </div>

        <div className="casino-box-row">
          <div className={`casino-bl-box${suspCls(even)}`}>
            <div
              className="back casino-bl-box-item"
              onClick={isSusp(even) ? undefined : () => onBet(even.sid, 'back', even.b, even)}
            >
              <span className="casino-box-odd">Even</span>
              {' '}
              <span className={`casino-book ${bookCls(bEven)}`}>{fmtBook(bEven)}</span>
            </div>
          </div>
          <div className={`casino-bl-box${suspCls(odd)}`}>
            <div
              className="back casino-bl-box-item"
              onClick={isSusp(odd) ? undefined : () => onBet(odd.sid, 'back', odd.b, odd)}
            >
              <span className="casino-box-odd">Odd</span>
              {' '}
              <span className={`casino-book ${bookCls(bOdd)}`}>{fmtBook(bOdd)}</span>
            </div>
          </div>
          <div className={`casino-bl-box${suspCls(black)}`}>
            <div
              className="back casino-bl-box-item casino-card-img"
              onClick={isSusp(black) ? undefined : () => onBet(black.sid, 'back', black.b, black)}
            >
              <span>
                <img src={`${CARD_IMG_BASE}/spade.png`} />
                {' '}
                <img src={`${CARD_IMG_BASE}/club.png`} />
              </span>
              {' '}
              <span className={`casino-book ${bookCls(bBlack)}`}>{fmtBook(bBlack)}</span>
            </div>
          </div>
          <div className={`casino-bl-box${suspCls(red)}`}>
            <div
              className="back casino-bl-box-item casino-card-img"
              onClick={isSusp(red) ? undefined : () => onBet(red.sid, 'back', red.b, red)}
            >
              <span>
                <img src={`${CARD_IMG_BASE}/heart.png`} />
                {' '}
                <img src={`${CARD_IMG_BASE}/diamond.png`} />
              </span>
              {' '}
              <span className={`casino-book ${bookCls(bRed)}`}>{fmtBook(bRed)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="casino-box cards-box">
        <div className="w-100">
          <div className="casino-odds casino-cards-odds-title">
            <div className="text-center w-100">
              <b>{cardOdds ?? 0}</b>
              {' '}
              {/* card min/max from the feed (dt20 100-5K, pdt20 1K-10K) */}
              <Range rangeId={cardRangeId} min={cardItems[0]?.min ?? 100} max={cardItems[0]?.max ?? 5000} />
            </div>
          </div>
          <div className="casino-cards text-center mt-1">
            {CARD_LABELS.map((rank, i) => {
              const item = cardItems[i];
              const book = bookOf(exposures, item?.nat, item?.sid);
              return (
                <div key={rank} className="casino-card-item">
                  <div
                    className={`card-image${suspCls(item)}`}
                    onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'back', item.b, item)}
                  >
                    <img src={cardSrc(rank)} />
                  </div>
                  <div className={`casino-book ${bookCls(book)}`}>{fmtBook(book)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
});

const BetTableDT20 = memo(function BetTableDT20({ gameData, onBet, exposures }) {
  const sub = gameData?.sub || [];
  const remark = gameData?.remark ?? '';
  const handleBet = onBet || (() => {});

  const dragon = findByNat(sub, 'Dragon');
  const tiger = findByNat(sub, 'Tiger');
  const tie = findByNat(sub, 'Tie');
  const pair = findByNat(sub, 'Pair');

  const dragonEven = findByNat(sub, 'Dragon Even');
  const dragonOdd = findByNat(sub, 'Dragon Odd');
  const dragonBlack = findByNat(sub, 'Dragon Black');
  const dragonRed = findByNat(sub, 'Dragon Red');

  const tigerEven = findByNat(sub, 'Tiger Even');
  const tigerOdd = findByNat(sub, 'Tiger Odd');
  const tigerBlack = findByNat(sub, 'Tiger Black');
  const tigerRed = findByNat(sub, 'Tiger Red');

  const makeCardItem = (side, rank, sideItem) => {
    const apiRank = rank === 'A' ? '1' : rank;
    const found =
      findByNat(sub, `${side} ${rank}`) ||
      findByNat(sub, `${side} Card ${rank}`) ||
      findByNat(sub, `${side} ${apiRank}`) ||
      findByNat(sub, `${side} Card ${apiRank}`);
    if (found) return found;
    return {
      sid: `${side}_${rank}`,
      nat: `${side} Card ${rank}`,
      b: pair?.b ?? 0,
      gstatus: sideItem?.gstatus || 'SUSPENDED',
      min: pair?.min || sideItem?.min,
      max: pair?.max || sideItem?.max,
    };
  };

  const dragonCards = CARD_LABELS.map((rank) => makeCardItem('Dragon', rank, dragon));
  const tigerCards = CARD_LABELS.map((rank) => makeCardItem('Tiger', rank, tiger));

  return (
    <>
      <div className="text-right w-100 pr">
        <Range rangeId="demo1" label="Range:" min={dragon?.min ?? 100} max={dragon?.max ?? 300000} />
      </div>

      <div className="dtobx-top">
        <TopBox cls="dragon-box" label="Dragon" item={dragon} exposures={exposures} onBet={handleBet} />
        <TopBox cls="tiebox" label="Tie" item={tie} exposures={exposures} onBet={handleBet} />
        <TopBox cls="tiger-box" label="Tiger" item={tiger} exposures={exposures} onBet={handleBet} />
        <TopBox cls="pair-box" label="Pair" item={pair} exposures={exposures} onBet={handleBet} />
      </div>

      <div className="teen1daycasino-container mt-5">
        <div className="teen1dayleft">
          <DragonTigerSide
            exposures={exposures}
            label="Dragon"
            labelClass="text-playera"
            oddsRangeId="demo2"
            cardRangeId="demo3"
            even={dragonEven}
            odd={dragonOdd}
            black={dragonBlack}
            red={dragonRed}
            cardItems={dragonCards}
            cardOdds={pair?.b}
            onBet={handleBet}
          />
        </div>

        <div className="teen1daycenter"></div>

        <div className="teen1dayright">
          <DragonTigerSide
            exposures={exposures}
            label="Tiger"
            labelClass="text-playerb"
            oddsRangeId="demo4"
            cardRangeId="demo5"
            even={tigerEven}
            odd={tigerOdd}
            black={tigerBlack}
            red={tigerRed}
            cardItems={tigerCards}
            cardOdds={pair?.b}
            onBet={handleBet}
          />
        </div>
      </div>

      {/* reference (inline bundle): remark block only while the feed's remark is
          non-empty — `casinoData.remark && "" != casinoData.remark`. dt20 sends
          " " (shown, empty marquee); pdt20 sends "" (no block, capture). */}
      {remark !== '' && (
        <div className="casino-remark mt-3">
          <div className="remark-icon"><img src={REMARK_ICON} /></div>
          {' '}
          <marquee>{remark}</marquee>
        </div>
      )}
    </>
  );
});

export default BetTableDT20;
